/**
 * Comida — MOBILE.
 *
 * El camino principal es un campo de texto: escribís lo que comiste como se lo contarías a alguien,
 * y la IA estima. Es lo más rápido de hacer con una mano, y no exige buscar alimentos en una base
 * ni pesar nada.
 *
 * Nada se guarda sin confirmar, y lo que el modelo estimó con poca confianza viene resaltado. Con
 * la comida la incertidumbre es mayor que con la balanza: "un plato de tallarines" puede ser 400 o
 * 900 kcal. La UI lo dice en vez de esconderlo.
 *
 * El celular también CONSULTA: se elige el día que se está mirando (y en el que se va a anotar), y
 * el registro de las últimas dos semanas está a la vista aunque el día esté vacío. Antes esto era
 * sólo-hoy y sólo-escritura, y el promedio vivía únicamente en el escritorio.
 */

import { useRef, useState, type ReactNode } from 'react';
import { formatearNumero, type MomentoDelDia } from '@mg/core';

import { useNutricion, type FilaBorrador } from '../features/useNutricion.ts';
import { Aviso, Campo, Cifra, Icono, Insignia, Pila, Vacio, estadosDeRatio } from '../ui/componentes.tsx';
import { useRuta } from '../ui/router.tsx';

const EJEMPLOS = [
  '2 huevos revueltos y una tostada con palta',
  '150 g de pollo a la plancha con arroz',
  'un yogur griego y un puñado de almendras',
];

export function NutricionMobile(): ReactNode {
  const n = useNutricion();

  if (n.datos.cargando) return <Vacio>Cargando…</Vacio>;

  if (n.paso === 'revisar') return <Revisar n={n} />;

  if (n.paso === 'guardado') {
    return (
      <>
        <Aviso tono="info">
          Guardado en {n.dondeQueda} ·{' '}
          <span className="lectura">{formatearNumero(n.totalesBorrador.kcal, 0)} kcal</span>,{' '}
          <span className="lectura">{formatearNumero(n.totalesBorrador.proteina_g, 0)} g</span> de proteína.
        </Aviso>
        <button type="button" className="boton boton--principal boton--registrar" onClick={n.reiniciar}>
          Agregar otra comida
        </button>
      </>
    );
  }

  return (
    <>
      <ResumenDelDia n={n} />
      <Agregar n={n} />
      <AvisoBorrado n={n} />
      <ListaDelDia n={n} />
    </>
  );
}

// ---------------------------------------------------------------------------

function ResumenDelDia({ n }: { n: ReturnType<typeof useNutricion> }): ReactNode {
  const { ir } = useRuta();
  const { totales, objetivos, proteina, constanciaRegistro, promedio7 } = n;
  const hay = totales.items > 0;
  const esHoy = n.dia === n.datos.hoy;

  return (
    <section className="tarjeta">
      <div className="fila fila--entre">
        <span className="eyebrow">{esHoy ? 'Hoy' : n.etiquetaDia}</span>
        {totales.confianzaMinima !== null && totales.confianzaMinima < n.umbralConfianza && (
          <Insignia tono="ambar">estimado</Insignia>
        )}
      </div>

      <div className="fila" style={{ gap: 'var(--e2)', marginTop: 'var(--e2)' }}>
        <input
          className="campo crece"
          type="date"
          value={n.dia}
          onChange={(e) => n.setDia(e.target.value)}
          aria-label="Día que estás mirando"
        />
        {!esHoy && (
          <button type="button" className="boton" onClick={() => n.setDia(n.datos.hoy)}>
            Hoy
          </button>
        )}
      </div>
      <p className="nota--fina" style={{ marginTop: 4 }}>
        Lo que agregues abajo se anota en este día.
      </p>

      {!hay ? (
        <p className="nota" style={{ marginTop: 'var(--e3)' }}>
          {esHoy ? 'Todavía no anotaste nada hoy.' : 'Ese día no tiene nada anotado.'} Escribí abajo lo que
          comiste.
        </p>
      ) : (
        <>
          <div className="fila fila--entre" style={{ alignItems: 'flex-end', marginTop: 'var(--e3)' }}>
            <div>
              <Cifra valor={totales.proteina_g} unidad="g" decimales={0} />
              <p className="nota--fina">proteína</p>
            </div>
            <div style={{ textAlign: 'right' }}>
              <Cifra valor={totales.kcal} unidad="kcal" decimales={0} />
              <p className="nota--fina">{totales.items} {totales.items === 1 ? 'alimento' : 'alimentos'}</p>
            </div>
          </div>

          {objetivos.proteina_g ? (
            <div style={{ marginTop: 'var(--e3)' }}>
              <Pila
                estados={estadosDeRatio(totales.proteina_g / objetivos.proteina_g, 20)}
                alto={16}
                etiqueta={`${formatearNumero(totales.proteina_g, 0)} de ${objetivos.proteina_g} g de proteína`}
              />
              <p className="nota--fina" style={{ marginTop: 4 }}>
                {formatearNumero(totales.proteina_g, 0)} de {objetivos.proteina_g} g — tu objetivo
              </p>
            </div>
          ) : (
            <div style={{ marginTop: 'var(--e3)' }}>
              <p className="nota--fina">
                Si querés ver esto contra un objetivo, ponelo vos. La app no calcula uno.
              </p>
              <button
                type="button"
                className="boton boton--chico"
                style={{ marginTop: 'var(--e2)' }}
                onClick={() => ir('ajustes', 'objetivos')}
              >
                Poner un objetivo
              </button>
            </div>
          )}
        </>
      )}

      <hr className="separador" />

      <p className="nota">
        Anotaste {constanciaRegistro.diasConRegistro} de los últimos {constanciaRegistro.diasDeVentana} días.
      </p>
      <div style={{ marginTop: 'var(--e2)' }}>
        <Pila
          estados={estadosDeRatio(constanciaRegistro.proporcion, constanciaRegistro.diasDeVentana)}
          alto={12}
          etiqueta={`Días anotados de los últimos ${constanciaRegistro.diasDeVentana}`}
        />
      </div>
      <p className="nota--fina" style={{ marginTop: 6 }}>
        Es información sobre el registro, no un reproche.
      </p>

      {promedio7.kcal !== null && promedio7.proteina_g !== null && (
        <p className="nota" style={{ marginTop: 'var(--e3)' }}>
          Promedio de 7 días: <span className="lectura">{formatearNumero(promedio7.kcal, 0)} kcal</span> ·{' '}
          <span className="lectura">{formatearNumero(promedio7.proteina_g, 0)} g</span> de proteína
          <span style={{ color: promedio7.registroEscaso ? 'var(--ambar)' : undefined }}>
            {' '}
            — de {promedio7.diasConRegistro} días anotados.
          </span>
        </p>
      )}

      {proteina.gramosPorKg !== null && (
        <p className="nota--fina" style={{ marginTop: 4 }}>
          Son <span className="lectura">{formatearNumero(proteina.gramosPorKg, 2)} g/kg</span> de peso corporal.
        </p>
      )}
    </section>
  );
}

/**
 * El momento del día, con la hora que lo sugirió a la vista.
 *
 * La línea de abajo no es decoración: en el paso de escribir, la hora que se va a guardar no se
 * ve en ningún otro lado, y sin ella el usuario no tiene cómo darse cuenta de que el momento
 * marcado no es el que corresponde. Además dice de dónde salió la elección, que es lo que explica
 * por qué cambiar la hora a veces mueve el momento y a veces no.
 */
function ChipsDeMomento({ n }: { n: ReturnType<typeof useNutricion> }): ReactNode {
  return (
    <>
      <div className="fila" style={{ gap: 'var(--e2)', flexWrap: 'wrap' }}>
        {n.momentos.map((m) => (
          <button
            key={m}
            type="button"
            className={n.momento === m ? 'boton boton--chico boton--principal' : 'boton boton--chico'}
            onClick={() => n.setMomento(m as MomentoDelDia)}
            style={{ flex: 1, minWidth: 72, minHeight: 48 }}
            aria-pressed={n.momento === m}
          >
            {n.nombreMomento[m]}
          </button>
        ))}
      </div>
      <p className="nota--fina" style={{ marginTop: 6 }}>
        {n.momentoEsSugerencia ? (
          <>
            Lo sugiere la hora (<span className="lectura">{n.horaAGuardar}</span>). Tocá otro si va en
            otra comida.
          </>
        ) : (
          <>
            Lo elegiste vos: queda en {n.nombreMomento[n.momento]}, a las{' '}
            <span className="lectura">{n.horaAGuardar}</span>.
          </>
        )}
      </p>
    </>
  );
}

function Agregar({ n }: { n: ReturnType<typeof useNutricion> }): ReactNode {
  const input = useRef<HTMLInputElement>(null);
  const [ejemplo] = useState(() => EJEMPLOS[Math.floor(Math.random() * EJEMPLOS.length)]!);

  if (n.paso === 'estimando') {
    return (
      <section className="tarjeta">
        {n.vistaPrevia && (
          <img
            src={n.vistaPrevia}
            alt="La foto que se está estimando"
            style={{ width: '100%', borderRadius: 'var(--r)', marginBottom: 'var(--e3)' }}
          />
        )}
        <p className="nota" style={{ textAlign: 'center' }}>
          Estimando…
        </p>
      </section>
    );
  }

  const faltaTexto = !n.texto.trim();

  return (
    <section className="tarjeta">
      <span className="eyebrow">¿Qué comiste?</span>

      <textarea
        className="campo campo--texto"
        style={{ minHeight: 88, padding: 'var(--e3)', resize: 'vertical', marginTop: 'var(--e2)' }}
        // El ejemplo sin "Ej.:" se lee como una comida ya escrita, y entonces el botón apagado
        // parece la app rota.
        placeholder={`Ej.: ${ejemplo}`}
        value={n.texto}
        onChange={(e) => n.setTexto(e.target.value)}
        aria-label="Qué comiste"
      />

      <div style={{ marginTop: 'var(--e3)' }}>
        <ChipsDeMomento n={n} />
      </div>

      {n.error && (
        <div style={{ marginTop: 'var(--e3)' }}>
          <Aviso tono="importante">
            {n.error.mensaje}
            {n.error.sinRed && (
              <button
                type="button"
                className="boton"
                style={{ width: '100%', marginTop: 'var(--e2)' }}
                onClick={n.empezarAMano}
              >
                A mano
              </button>
            )}
          </Aviso>
        </div>
      )}

      <button
        type="button"
        className="boton boton--principal boton--registrar"
        style={{ marginTop: 'var(--e3)' }}
        disabled={faltaTexto}
        onClick={() => void n.estimarTexto()}
      >
        Estimar y revisar
      </button>

      {faltaTexto && (
        <p className="nota--fina" style={{ marginTop: 4, textAlign: 'center' }}>
          Escribí qué comiste para poder estimar.
        </p>
      )}

      <input
        ref={input}
        type="file"
        accept="image/*"
        capture="environment"
        style={{ display: 'none' }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void n.estimarFoto(f);
        }}
      />

      <div className="fila" style={{ gap: 'var(--e2)', marginTop: 'var(--e2)' }}>
        <button type="button" className="boton boton--fantasma crece" onClick={() => input.current?.click()}>
          <Icono nombre="camara" tamano={18} />
          Foto del plato
        </button>
        <button type="button" className="boton boton--fantasma crece" onClick={n.empezarAMano}>
          A mano
        </button>
      </div>

      <p className="nota--fina" style={{ marginTop: 'var(--e3)' }}>
        Decí la cantidad si la sabés ("150 g de pollo"): la estimación mejora mucho. Si no, la app
        asume una porción típica y te lo dice.
      </p>
    </section>
  );
}

/**
 * La vuelta atrás del borrado. Vive fuera de la lista a propósito: borrar el último alimento del
 * día deja la lista vacía, y con el aviso adentro el "Deshacer" se iría junto con la fila.
 */
function AvisoBorrado({ n }: { n: ReturnType<typeof useNutricion> }): ReactNode {
  if (!n.borrado) return null;

  return (
    <Aviso tono="atencion" titulo="Borrado">
      «{n.borrado.descripcion}» se borró de este teléfono. Si ya se había subido, vuelve en la
      próxima sincronización.
      <div className="fila" style={{ gap: 'var(--e2)', marginTop: 'var(--e3)' }}>
        <button type="button" className="boton crece" onClick={() => void n.deshacerBorrado()}>
          Deshacer
        </button>
        <button type="button" className="boton boton--fantasma" onClick={n.olvidarBorrado}>
          Listo
        </button>
      </div>
    </Aviso>
  );
}

function ListaDelDia({ n }: { n: ReturnType<typeof useNutricion> }): ReactNode {
  if (n.delDia.length === 0) return null;

  return (
    <section className="columna" style={{ gap: 'var(--e2)' }}>
      <span className="eyebrow">{n.dia === n.datos.hoy ? 'Lo de hoy' : 'Lo anotado ese día'}</span>

      {n.momentos
        .filter((m) => n.delDia.some((a) => a.momento === m))
        .map((m) => (
          <div key={m} className="tarjeta" style={{ padding: 'var(--e3)' }}>
            <div className="fila fila--entre">
              <strong style={{ fontSize: 14 }}>{n.nombreMomento[m]}</strong>
              <span className="lectura nota--fina">
                {formatearNumero(n.totales.porMomento[m].kcal, 0)} kcal ·{' '}
                {formatearNumero(n.totales.porMomento[m].proteina_g, 0)} g P
              </span>
            </div>

            <div className="columna" style={{ gap: 6, marginTop: 'var(--e2)' }}>
              {n.delDia
                .filter((a) => a.momento === m)
                .map((a) => (
                  <div key={a.id} className="fila fila--entre" style={{ gap: 'var(--e2)' }}>
                    <span style={{ fontSize: 13, flex: 1 }}>
                      {a.descripcion}
                      {a.confianza !== null && a.confianza < n.umbralConfianza && (
                        <>
                          {' '}
                          <Insignia tono="ambar">±</Insignia>
                        </>
                      )}
                    </span>
                    <span className="lectura nota--fina" style={{ whiteSpace: 'nowrap' }}>
                      {formatearNumero(a.kcal, 0)} / {formatearNumero(a.proteina_g, 0)} g
                    </span>
                    <button
                      type="button"
                      className="boton boton--chico boton--fantasma"
                      style={{ minHeight: 48, minWidth: 48, padding: 0 }}
                      onClick={() => void n.borrarConDeshacer(a)}
                      aria-label={`Borrar ${a.descripcion}`}
                    >
                      <Icono nombre="cerrar" tamano={16} />
                    </button>
                  </div>
                ))}
            </div>
          </div>
        ))}
    </section>
  );
}

function Revisar({ n }: { n: ReturnType<typeof useNutricion> }): ReactNode {
  return (
    <>
      <Aviso tono="atencion" titulo="Revisá antes de guardar">
        Son estimaciones. Lo que está en ámbar el modelo lo calculó con poca certeza — corregilo si
        sabés el valor real.
      </Aviso>

      <section className="tarjeta">
        <span className="eyebrow">Dónde va a quedar</span>
        {/* `horaAGuardar` y no `hora`: si el campo de abajo quedó vacío, esta línea tiene que
            decir la hora con la que se va a guardar, no un blanco. */}
        <p style={{ marginTop: 4 }}>
          <strong>{n.nombreMomento[n.momento]}</strong> · {n.etiquetaDia}{' '}
          <span className="lectura">{n.horaAGuardar}</span>
        </p>

        <div style={{ marginTop: 'var(--e3)' }}>
          <ChipsDeMomento n={n} />
        </div>

        <div className="fila" style={{ gap: 'var(--e2)', marginTop: 'var(--e3)' }}>
          <div className="crece">
            <Campo etiqueta="Día" texto type="date" value={n.dia} onChange={(e) => n.setDia(e.target.value)} />
          </div>
          <div style={{ width: 110 }}>
            <Campo etiqueta="Hora" texto type="time" value={n.hora} onChange={(e) => n.setHora(e.target.value)} />
          </div>
        </div>
      </section>

      {n.noEstimables.length > 0 && (
        <Aviso tono="info" titulo="Esto no lo pudo estimar">
          {n.noEstimables.join(' · ')}. Agregalo a mano si querés contarlo.
        </Aviso>
      )}

      <div className="columna" style={{ gap: 'var(--e3)' }}>
        {n.filas.map((f, i) => (
          <FilaEditable key={i} f={f} i={i} n={n} />
        ))}
      </div>

      <button type="button" className="boton boton--fantasma" onClick={n.agregarFila}>
        <Icono nombre="mas" tamano={18} />
        Agregar otro alimento
      </button>

      <section className="tarjeta">
        <div className="fila fila--entre">
          <div>
            <span className="eyebrow">Total de esta comida</span>
            <p className="lectura" style={{ fontSize: 18 }}>
              {formatearNumero(n.totalesBorrador.kcal, 0)} kcal ·{' '}
              {formatearNumero(n.totalesBorrador.proteina_g, 0)} g de proteína
            </p>
          </div>
        </div>
      </section>

      {n.errores.length > 0 && (
        <Aviso tono="importante" titulo="Eso no se puede guardar">
          {[...new Set(n.errores.map((h) => h.mensaje))].join(' ')}
        </Aviso>
      )}

      {n.errores.length === 0 && n.aConfirmar.length > 0 && (
        <Aviso tono="atencion">
          {[...new Set(n.aConfirmar.map((h) => h.mensaje))].join(' ')}
          <label className="fila" style={{ marginTop: 'var(--e2)', gap: 8 }}>
            <input
              type="checkbox"
              checked={n.confirmado}
              onChange={(e) => n.setConfirmado(e.target.checked)}
              style={{ width: 22, height: 22 }}
            />
            <span>Está bien así</span>
          </label>
        </Aviso>
      )}

      {/* El fallo al guardar se pinta acá: si no, el botón vuelve de "Guardando…" y no cambia
          nada, que se lee como que el toque no entró. */}
      {n.error && <Aviso tono="importante">{n.error.mensaje}</Aviso>}

      <button
        type="button"
        className="boton boton--principal boton--registrar"
        disabled={!n.puedeGuardar || n.guardando}
        onClick={() => void n.confirmar()}
      >
        {n.guardando ? 'Guardando…' : 'Confirmar y guardar'}
      </button>

      <button type="button" className="boton boton--fantasma boton--chico" onClick={n.reiniciar}>
        Descartar
      </button>
    </>
  );
}

function FilaEditable({
  f,
  i,
  n,
}: {
  f: FilaBorrador;
  i: number;
  n: ReturnType<typeof useNutricion>;
}): ReactNode {
  const v = n.validaciones[i];
  const conError = v ? !v.ok : false;

  return (
    <div className="tarjeta" style={{ borderColor: conError ? 'var(--ladrillo)' : undefined }}>
      <div className="fila fila--entre" style={{ marginBottom: 'var(--e2)' }}>
        <div className="fila" style={{ gap: 6 }}>
          {f.revisar && <Insignia tono="ambar">revisar</Insignia>}
          {f.confianza !== null && (
            <span className="nota--fina lectura">{Math.round(f.confianza * 100)} % de certeza</span>
          )}
        </div>
        {n.filas.length > 1 && (
          <button
            type="button"
            className="boton boton--chico boton--fantasma"
            style={{ minHeight: 48, minWidth: 48, padding: 0 }}
            onClick={() => n.quitarFila(i)}
            aria-label="Quitar este alimento"
          >
            <Icono nombre="cerrar" tamano={16} />
          </button>
        )}
      </div>

      <Campo
        etiqueta="Qué es"
        texto
        value={f.descripcion}
        onChange={(e) => n.cambiarFila(i, { descripcion: e.target.value })}
      />

      <div className="fila" style={{ gap: 'var(--e2)', marginTop: 'var(--e2)' }}>
        <div className="crece">
          <Campo
            etiqueta="kcal"
            inputMode="numeric"
            revisar={f.revisar}
            value={f.kcal}
            onChange={(e) => n.cambiarFila(i, { kcal: e.target.value })}
          />
        </div>
        <div className="crece">
          <Campo
            etiqueta="Proteína g"
            inputMode="decimal"
            revisar={f.revisar}
            value={f.proteina_g}
            onChange={(e) => n.cambiarFila(i, { proteina_g: e.target.value })}
          />
        </div>
      </div>

      <div className="fila" style={{ gap: 'var(--e2)', marginTop: 'var(--e2)' }}>
        <div className="crece">
          <Campo
            etiqueta="Carbos g"
            inputMode="decimal"
            value={f.carbohidratos_g}
            onChange={(e) => n.cambiarFila(i, { carbohidratos_g: e.target.value })}
          />
        </div>
        <div className="crece">
          <Campo
            etiqueta="Grasa g"
            inputMode="decimal"
            value={f.grasa_g}
            onChange={(e) => n.cambiarFila(i, { grasa_g: e.target.value })}
          />
        </div>
      </div>

      {conError && (
        <p className="nota--fina" style={{ color: 'var(--ladrillo)', marginTop: 'var(--e2)' }}>
          {v!.hallazgos.find((h) => h.severidad === 'error')?.mensaje}
        </p>
      )}
    </div>
  );
}
